import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from "@mui/material";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { useTheme } from "@mui/material/styles";
import { IoHomeOutline } from "react-icons/io5";
import { MdBuild, MdOutlineAccountTree, MdOutlineBrush, MdOutlineGridView, MdOutlineLocalShipping, MdOutlineMenuBook, MdOutlinePeopleAlt, MdOutlinePrecisionManufacturing, MdOutlineSettings } from "react-icons/md";

import { Can } from "../../../Components/Can";
import { adminVersion } from "../../../Services/api";

interface HCPSidebarProps {
  open: boolean;
  onClose: () => void;
}

interface IMenuItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  disabled?: boolean;
  permission?: string;
}

interface IMenuGroup {
  group: string | null;
  items: IMenuItem[];
}

const MENU: IMenuGroup[] = [
  {
    group: null,
    items: [
      { title: 'Início', path: '/hcp/dashboard', icon: <IoHomeOutline size={20} /> },
      { title: 'Sair para o HUB', path: '/dashboard', icon: <MdOutlineGridView size={20} /> },
    ],
  },
  {
    group: 'Fluxos',
    items: [
      { title: 'Gestão de Fluxo',                  path: '/hcp/gestao-fluxo',      icon: <MdOutlineAccountTree size={20} />,        permission: 'feature:hcp-gestao-fluxo:manage' },
      { title: '[FLUXO 1] Arte Final',             path: '/hcp/arte-andamento',    icon: <MdOutlineBrush size={20} />,             permission: 'feature:hcp-arte:manage' },
      { title: '[FLUXO 2] Montagem de Cartelas',   path: '/hcp/montagem-cartelas', icon: <MdOutlineGridView size={20} />,            permission: 'feature:hcp-montagem:manage' },
      { title: '[FLUXO 3] Produção',               path: '/hcp/producao',          icon: <MdOutlinePrecisionManufacturing size={20} /> },
      { title: '[FLUXO 4] Máquinas',               path: '/hcp/maquinas',         icon: <MdBuild size={20} /> },
      { title: '[FLUXO 5] Logística',              path: '/hcp/logistica',        icon: <MdOutlineLocalShipping size={20} /> },
    ],
  },
  {
    group: 'Gestão',
    items: [
      { title: 'Colaboradores',  path: '/hcp/colaboradores',  icon: <MdOutlinePeopleAlt size={20} />, permission: 'feature:hcp-colab:manage' },
      { title: 'Configurações',  path: '/hcp/configuracoes',  icon: <MdOutlineSettings size={20} />,  permission: 'feature:hcp-config:manage' },
    ],
  },
  {
    group: 'Ajuda',
    items: [
      { title: 'Manual do Usuário', path: '/hcp/manual', icon: <MdOutlineMenuBook size={20} /> },
    ],
  },
];

export default function HCPSidebar({ open, onClose }: HCPSidebarProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleDrawer = (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event &&
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    onClose();
  };

  const renderItem = (item: IMenuItem) => {
    const isActive = !item.disabled && location.pathname.startsWith(item.path);

    const button = (
      <ListItemButton
        onClick={() => !item.disabled && navigate(item.path)}
        selected={isActive}
        disabled={item.disabled}
        sx={{
          mx: 1.5,
          mb: 0.5,
          borderRadius: '12px',
          px: 2,
          py: 1,
          transition: 'all 0.2s ease',
          '&.Mui-selected': {
            backgroundColor: 'rgba(132, 168, 2, 0.12)',
            color: theme.palette.primary.dark,
            '&:hover': { backgroundColor: 'rgba(132, 168, 2, 0.20)' },
            '& .MuiListItemIcon-root': { color: theme.palette.primary.main }
          },
          '&:hover:not(.Mui-disabled)': {
            backgroundColor: 'rgba(132, 168, 2, 0.06)',
            color: theme.palette.primary.main,
            transform: 'translateX(4px)'
          },
          color: isActive ? theme.palette.primary.dark : 'text.secondary',
          opacity: item.disabled ? 0.5 : 1,
        }}
      >
        <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.title}
          primaryTypographyProps={{ 
            fontSize: 14, 
            fontWeight: isActive ? 700 : 500,
            letterSpacing: -0.2
          }}
        />
        {item.disabled && (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.55rem', 
              color: 'text.disabled', 
              ml: 1,
              fontWeight: 700,
              textTransform: 'uppercase',
              backgroundColor: 'rgba(0,0,0,0.04)',
              px: 0.8,
              py: 0.2,
              borderRadius: 1
            }}
          >
            breve
          </Typography>
        )}
      </ListItemButton>
    );

    const content = (
      <ListItem key={item.title} disablePadding>
        {item.disabled
          ? <Tooltip title="Em breve" placement="right">{button}</Tooltip>
          : button
        }
      </ListItem>
    );

    if (item.permission) {
      return (
        <Can permissions={[item.permission]} key={item.title}>
          {content}
        </Can>
      );
    }

    return content;
  };

  const list = () => (
    <Box sx={{ width: 300, height: '100%', display: 'flex', flexDirection: 'column' }} role="presentation" onClick={toggleDrawer} onKeyDown={toggleDrawer}>
      <Box sx={{ 
        p: 3, 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'flex-start',
        borderBottom: `1px solid ${theme.palette.divider}`,
        background: 'linear-gradient(135deg, #fefefe 0%, #f5f5f5 100%)'
      }}>
        <Typography variant="h6" color="primary" fontWeight={900} sx={{ letterSpacing: -0.5 }}>PAPELLO</Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mt: -0.5 }}>HCP - GESTÃO DE PRODUÇÃO</Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
        {MENU.map((section, i) => (
          <Box key={i} sx={{ mb: 2 }}>
            {section.group && (
              <Typography
                variant="caption"
                sx={{ px: 3, mb: 1, display: 'block', color: 'text.disabled', textTransform: 'uppercase', fontWeight: 800, letterSpacing: 1.5, fontSize: '0.6rem' }}
              >
                {section.group}
              </Typography>
            )}
            <List sx={{ pt: 0, pb: 0 }}>
              {section.items.map(renderItem)}
            </List>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>Versão {adminVersion} · Papello © 2026</Typography>
      </Box>
    </Box>
  );

  return (
    <SwipeableDrawer anchor="left" open={open} onClose={onClose} onOpen={() => {}}>
      {list()}
    </SwipeableDrawer>
  );
}
